/**
 * Debug logger that sends logs to the Vite dev server for writing to /tmp/map-debug.log
 * Only active in development mode
 */

const LOG_ENDPOINT = "/__debug_log";
const isDev = import.meta.env.DEV;

type LogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  source: "browser" | "map";
  message: string;
  data?: unknown;
}

function formatTimestamp(): string {
  return new Date().toISOString();
}

async function sendLog(entry: LogEntry): Promise<void> {
  if (!isDev) return;

  try {
    await fetch(LOG_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entry),
    });
  } catch {
    // Silently fail - don't want logging to break the app
  }
}

export const mapLogger = {
  info: (message: string, data?: unknown) => {
    const entry: LogEntry = {
      timestamp: formatTimestamp(),
      level: "INFO",
      source: "map",
      message,
      data,
    };
    console.log(`[MAP] ${message}`, data ?? "");
    void sendLog(entry);
  },

  warn: (message: string, data?: unknown) => {
    const entry: LogEntry = {
      timestamp: formatTimestamp(),
      level: "WARN",
      source: "map",
      message,
      data,
    };
    console.warn(`[MAP] ${message}`, data ?? "");
    void sendLog(entry);
  },

  error: (message: string, data?: unknown) => {
    const entry: LogEntry = {
      timestamp: formatTimestamp(),
      level: "ERROR",
      source: "map",
      message,
      data,
    };
    console.error(`[MAP] ${message}`, data ?? "");
    void sendLog(entry);
  },

  debug: (message: string, data?: unknown) => {
    const entry: LogEntry = {
      timestamp: formatTimestamp(),
      level: "DEBUG",
      source: "map",
      message,
      data,
    };
    console.debug(`[MAP] ${message}`, data ?? "");
    void sendLog(entry);
  },
};

// Capture unhandled errors related to WebGL
if (isDev && typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    if (
      event.message?.includes("WebGL") ||
      event.message?.includes("context")
    ) {
      mapLogger.error("Unhandled WebGL error", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
      });
    }
  });
}

export default mapLogger;
